import { useCallback, useMemo } from 'react';
import { useNavigate, Location } from 'react-router-dom';
import { lastProduct as getLastProduct } from '~/store/cookies';
import useContextualPathTransfomer from '~/hooks/use-contextual-path-transformer';
import { getBasePathName } from '~/utils/pathname';
import useConfiguration from '~/configuration/use-configuration';

type Options = {
	preserveSidebarState?: boolean;
	preserveContexts?: boolean;
	location?: Location;
};

function useRouterCallbacks() {
	const navigate = useNavigate();
	const transformPath = useContextualPathTransfomer();
	const { license } = useConfiguration();

	// memoize this, since it only needs to be calculated calculated once
	const basePath = useMemo(() => {
		const lastProd = getLastProduct() as string; // TODO: fix me when we move cookies to TS
		const theBasePathname = getBasePathName(license, lastProd);
		return lastProd ? theBasePathname() : '';
	}, [license]);

	const transformPathAndPush = useCallback(
		(path: string, state: Record<string, unknown> = {}, responsiveOptions?: Options) => {
			return navigate(transformPath(path, responsiveOptions), { state });
		},
		[navigate, transformPath]
	);

	const transformPathAndReplace = useCallback(
		(path: string, state: Record<string, string> = {}, responsiveOptions?: Options) => {
			const newOptions = { state, replace: true };
			return navigate(transformPath(path, responsiveOptions), newOptions);
		},
		[navigate, transformPath]
	);

	const pushBack = useCallback(() => {
		// TODO - history no longer available in v6, is there a better way
		if (window.history.length > 1) {
			return navigate(-1);
		}
		return navigate(basePath);
	}, [navigate, basePath]);

	return {
		pushBack,
		transformPathAndPush,
		transformPathAndReplace,
		basePath
	};
}

export default useRouterCallbacks;
